import "./ImportExportSettingsPane.css";
import "./settings-ui.css";
import { useEffect, useState } from "preact/hooks";
import { DashboardPage, createDashboardLayout } from "../../components/DashboardPage/DashboardPage.jsx";
import { FileDropZone } from "../../components/FileDropZone/FileDropZone.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { SegmentedControl } from "../../components/SegmentedControl/SegmentedControl.jsx";
import { StatusMessage } from "../../components/StatusMessage/StatusMessage.jsx";
import { localizeErrorMessage } from "../../lib/localizeErrorMessage.js";
import { useSessionStorageState } from "../../lib/useSessionStorageState.js";
import { parseBudgetImportCsv } from "../../lib/import/budgetCsvImport.js";
import { parseExpenseImportCsv } from "../../lib/import/expenseCsvImport.js";
import {
  PROFILE_DATA_EXPORT_VERSION,
  buildProfileDataExportText
} from "./profileDataExport.js";
import {
  buildBudgetExportText,
  buildExpenseExportText,
  downloadTextFile,
  exportDelimiterForFormat,
  exportMimeTypeForFormat
} from "./tabularExport.js";
import {
  getActiveBudgetScope,
  initializeWalletWatchDb,
  listBudgetCategoryOptions,
  listBudgetRows,
  listExpenseRows,
  listProfiles,
  listProfileYears,
  updateBudgetRows,
  updateExpenseRows
} from "../../lib/storage/walletWatchDb.js";
import {
  fetchCurrentProfileSnapshot,
  importProfileData
} from "../../lib/import/profileDataImport.js";

const importExportPaneLayout = createDashboardLayout({
  columns: [
    { size: "fill" },
    { size: "fill" }
  ],
  rows: [
    { size: "fill" }
  ],
  areas: [
    ["import", "export"]
  ]
});

function expenseImportRowKey(row) {
  return [
    row.date,
    row.amount,
    row.method,
    row.type,
    row.supplier,
    row.categoryId,
    row.service
  ].join("|");
}

function footerToneClass(type) {
  if (type === "success") {
    return "tone-success";
  }

  if (type === "warning") {
    return "tone-warning";
  }

  if (type === "error") {
    return "tone-error";
  }

  return "";
}

const EXPORT_SELECTION_SESSION_KEY = "wallet-watch:import-export-selection";
const ALL_DATA_EXPORT_OPTION = "all-data:json";

function parseExportSelection(selection) {
  const [dataset = "expenses", format = "csv"] = String(selection ?? "").split(":");
  return { dataset, format };
}

function safeFileNamePart(value, fallback) {
  const normalized = String(value ?? "")
    .trim()
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || fallback;
}

function createExportOptions(hasMultipleExportYears, t) {
  return [
    {
      value: ALL_DATA_EXPORT_OPTION,
      label: hasMultipleExportYears
        ? t("messages.importExportSettings.allDataAllYearsJson")
        : t("messages.importExportSettings.allDataJson")
    },
    {
      value: "budget:csv",
      label: hasMultipleExportYears
        ? t("messages.importExportSettings.budgetCurrentYearCsv")
        : t("messages.importExportSettings.budgetCsv")
    },
    {
      value: "budget:tsv",
      label: hasMultipleExportYears
        ? t("messages.importExportSettings.budgetCurrentYearTsv")
        : t("messages.importExportSettings.budgetTsv")
    },
    {
      value: "expenses:csv",
      label: hasMultipleExportYears
        ? t("messages.importExportSettings.expensesCurrentYearCsv")
        : t("messages.importExportSettings.expensesCsv")
    },
    {
      value: "expenses:tsv",
      label: hasMultipleExportYears
        ? t("messages.importExportSettings.expensesCurrentYearTsv")
        : t("messages.importExportSettings.expensesTsv")
    }
  ];
}

function getImportFooterMessage(importDataset, t) {
  if (importDataset === "budget") {
    return t("messages.importExportSettings.importBudgetFooter");
  }

  if (importDataset === "all-data") {
    return t("messages.importExportSettings.importAllDataFooter");
  }

  return t("messages.importExportSettings.importExpensesFooter");
}

function getExportFooterMessage(exportSelectionValue, hasMultipleExportYears, t) {
  if (exportSelectionValue === ALL_DATA_EXPORT_OPTION) {
    return hasMultipleExportYears
      ? t("messages.importExportSettings.exportAllDataAllYearsFooter")
      : t("messages.importExportSettings.exportAllDataFooter");
  }

  return hasMultipleExportYears
    ? t("messages.importExportSettings.exportCurrentYearFooter")
    : t("messages.importExportSettings.exportSingleYearFooter");
}

function getImportDropZoneProps(importDataset, isProfileVirgin, t) {
  if (importDataset === "all-data") {
    return {
      accept: ".json,application/json,text/json,text/plain",
      label: t("messages.importExportSettings.dropAllDataFileHere"),
      note: isProfileVirgin
        ? t("messages.importExportSettings.importAllDataOverwriteNote")
        : t("messages.importExportSettings.importAllDataNewProfileNote")
    };
  }

  if (importDataset === "budget") {
    return {
      accept: ".csv,.tsv,text/csv,text/tab-separated-values,text/plain",
      label: t("messages.importExportSettings.dropBudgetFileHere"),
      note: t("messages.importExportSettings.budgetFileNote")
    };
  }

  return {
    accept: ".csv,.tsv,text/csv,text/tab-separated-values,text/plain",
    label: t("messages.importExportSettings.dropExpensesFileHere"),
    note: t("messages.importExportSettings.expensesFileNote")
  };
}

export function ImportExportSettingsPane({ onDataImported, t = (key) => key }) {
  const [importDataset, setImportDataset] = useState("all-data");
  const [budgetImportYear, setBudgetImportYear] = useState(String(new Date().getFullYear()));
  const [importStatus, setImportStatus] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const [isProfileVirgin, setIsProfileVirgin] = useState(false);
  const [exportYearCount, setExportYearCount] = useState(1);
  const importDatasetOptions = [
    { value: "all-data", label: t("messages.importExportSettings.allData") },
    { value: "expenses", label: t("expenses") },
    { value: "budget", label: t("budget") }
  ];
  const hasMultipleExportYears = exportYearCount > 1;
  const exportOptions = createExportOptions(hasMultipleExportYears, t);
  const [exportSelection, setExportSelection] = useSessionStorageState(
    EXPORT_SELECTION_SESSION_KEY,
    exportOptions[0].value
  );
  const exportSelectionValue = exportOptions.some((option) => option.value === exportSelection)
    ? exportSelection
    : exportOptions[0].value;
  const importFooterMessage = getImportFooterMessage(importDataset, t);
  const exportFooterMessage = getExportFooterMessage(exportSelectionValue, hasMultipleExportYears, t);
  const importDropZoneProps = getImportDropZoneProps(importDataset, isProfileVirgin, t);

  useEffect(() => {
    let isMounted = true;

    async function loadExportYearCount() {
      await initializeWalletWatchDb();
      const scope = await getActiveBudgetScope();
      const years = await listProfileYears(scope.profileId, { includeArchived: true });

      if (isMounted) {
        setExportYearCount(Math.max(years.length, 1));
      }
    }

    async function refreshVirginState() {
      try {
        const snapshot = await fetchCurrentProfileSnapshot();
        if (isMounted) {
          setIsProfileVirgin(snapshot.isVirgin);
        }
      } catch (error) {
        console.error("Failed to load profile state.", error);
      }
    }

    loadExportYearCount().catch((error) => {
      console.error("Failed to load export year count.", error);
    });

    refreshVirginState().catch(() => {});

    return () => {
      isMounted = false;
    };
  }, []);

  async function importBudgetFile(file) {
    if (!file) {
      return;
    }

    setImportStatus({ type: "pending", message: `${t("messages.importExportSettings.importing")} ${file.name}...` });

    try {
      const text = await file.text();
      const importedRows = parseBudgetImportCsv(text);
      await initializeWalletWatchDb();
      const scope = await getActiveBudgetScope();
      const targetYear = String(budgetImportYear ?? "").trim();
      if (!/^\d{4}$/.test(targetYear)) {
        throw new Error(t("messages.importExportSettings.budgetImportYearMustBeFourDigits"));
      }

      const existingRows = await listBudgetRows(scope.profileId, targetYear);
      const existingIdsByCategory = new Map(existingRows.map((row) => [row.category, row.id]));
      const rowsForImport = importedRows.map((row) => ({
        ...row,
        id: existingIdsByCategory.get(row.category)
      }));

      await updateBudgetRows(scope.profileId, targetYear, rowsForImport);
      await onDataImported?.(scope, { navigateTo: "budget" });

      setImportStatus({
        type: "success",
        message: `${t("messages.importExportSettings.imported")} ${rowsForImport.length} ${t("messages.importExportSettings.budgetCategoriesInto")} ${targetYear} ${t("messages.importExportSettings.from")} ${file.name}.`
      });
    } catch (error) {
      setImportStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.importExportSettings.failedToImportBudgetCsv")
      });
    }
  }

  async function importExpenseFile(file) {
    if (!file) {
      return;
    }

    setImportStatus({ type: "pending", message: `${t("messages.importExportSettings.importing")} ${file.name}...` });

    try {
      const text = await file.text();
      await initializeWalletWatchDb();
      const scope = await getActiveBudgetScope();
      const parsedRows = parseExpenseImportCsv(text);
      const importedYears = [...new Set(parsedRows.map((row) => String(row.date).slice(0, 4)))];
      const rowsByYear = new Map(importedYears.map((year) => [year, []]));

      parsedRows.forEach((row) => {
        rowsByYear.get(String(row.date).slice(0, 4)).push(row);
      });

      for (const year of importedYears) {
        const [yearCategoryOptions, existingRows] = await Promise.all([
          listBudgetCategoryOptions(scope.profileId, year),
          listExpenseRows(scope.profileId, year)
        ]);
        const categoryIdsByName = new Map(yearCategoryOptions.map((option) => [option.label, option.value]));
        const existingRowKeyToId = new Map(
          existingRows.map((row) => [expenseImportRowKey(row), row.id])
        );
        const rowsForYear = rowsByYear.get(year).map((row) => {
          const categoryId = categoryIdsByName.get(row.category);
          if (!categoryId) {
            throw new Error(`${t("messages.importExportSettings.unknownExpenseCategoryFor")} ${year}: "${row.category}"`);
          }

          const normalizedRow = {
            ...row,
            categoryId
          };

          return {
            ...normalizedRow,
            id: existingRowKeyToId.get(expenseImportRowKey(normalizedRow))
          };
        });

        await updateExpenseRows(scope.profileId, year, rowsForYear);
      }

      await onDataImported?.(scope, { navigateTo: "expenses" });

      setImportStatus({
        type: "success",
        message: `${t("messages.importExportSettings.imported")} ${parsedRows.length} ${t("expenses")} ${t("messages.importExportSettings.across")} ${importedYears.length} ${importedYears.length === 1 ? t("year") : t("years")} ${t("messages.importExportSettings.from")} ${file.name}.`
      });
    } catch (error) {
      setImportStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.importExportSettings.failedToImportExpenseCsv")
      });
    }
  }

  async function importAllDataFile(file) {
    if (!file) {
      return;
    }

    setImportStatus({ type: "pending", message: `${t("messages.importExportSettings.importing")} ${file.name}...` });

    try {
      const text = await file.text();
      let payload;

      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error("Profile data file is not valid JSON.");
      }

      const result = await importProfileData(payload);
      await onDataImported?.(result.scope, { navigateTo: "budget" });
      setIsProfileVirgin(false);

      if (result.importedIntoCurrent) {
        setImportStatus({
          type: "success",
          message: `${t("messages.importExportSettings.importedIntoCurrentProfile")} ${t("messages.importExportSettings.from")} ${file.name}.`
        });
      } else {
        setImportStatus({
          type: "success",
          message: `${t("messages.importExportSettings.imported")} ${result.profile?.name ?? ""} ${t("messages.importExportSettings.intoNewProfile")} ${t("messages.importExportSettings.from")} ${file.name}.`
        });
      }
    } catch (error) {
      setImportStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.importExportSettings.failedToImportAllDataJson")
      });
    }
  }

  async function handleImportFile(file) {
    if (importDataset === "all-data") {
      await importAllDataFile(file);
      return;
    }

    if (importDataset === "budget") {
      await importBudgetFile(file);
      return;
    }

    await importExpenseFile(file);
  }

  async function handleExport() {
    try {
      await initializeWalletWatchDb();
      const scope = await getActiveBudgetScope();
      const { dataset, format } = parseExportSelection(exportSelectionValue);
      const delimiter = exportDelimiterForFormat(format);
      const mimeType = exportMimeTypeForFormat(format);

      if (dataset === "all-data" && format === "json") {
        const [profiles, years] = await Promise.all([
          listProfiles(),
          listProfileYears(scope.profileId, { includeArchived: true })
        ]);
        const profile = profiles.find((entry) => entry.id === scope.profileId) ?? {
          id: scope.profileId,
          name: scope.profileId
        };
        const budgets = [];
        const expenses = [];

        for (const yearRecord of years) {
          const year = String(yearRecord.year ?? "").trim();

          if (!year) {
            continue;
          }

          const [budgetRows, expenseRows] = await Promise.all([
            listBudgetRows(scope.profileId, year),
            listExpenseRows(scope.profileId, year)
          ]);

          budgets.push({
            year,
            rows: budgetRows.map((row) => ({
              id: row.id,
              category: row.category,
              archived: Boolean(row.archived),
              archivedAt: row.archivedAt ?? null,
              ongoingBudget: Number(row.ongoingBudget ?? 0),
              irregularBudget: Number(row.irregularBudget ?? 0)
            }))
          });
          expenses.push({
            year,
            rows: expenseRows.map((row) => ({
              id: row.id,
              date: row.date ?? "",
              amount: Number(row.amount ?? 0),
              method: row.method ?? "",
              type: row.type ?? "",
              supplier: row.supplier ?? "",
              categoryId: row.categoryId ?? "",
              category: row.category ?? "",
              service: row.service ?? ""
            }))
          });
        }

        const text = buildProfileDataExportText({
          version: PROFILE_DATA_EXPORT_VERSION,
          exportedAt: new Date().toISOString(),
          activeYear: scope.year,
          profile: {
            id: profile.id,
            name: profile.name,
            source: profile.source ?? "local"
          },
          years: years.map((yearRecord) => ({
            year: yearRecord.year,
            archived: Boolean(yearRecord.archivedAt),
            archivedAt: yearRecord.archivedAt ?? null
          })),
          budgets,
          expenses
        });
        const filename = `wallet-watch-budget-expenses-${safeFileNamePart(profile.name, scope.profileId)}.json`;

        downloadTextFile(filename, text, mimeType);
        setExportStatus({
          type: "success",
          message: `${t("messages.importExportSettings.exported")} ${years.length} ${years.length === 1 ? t("year") : t("years")} ${t("messages.importExportSettings.to")} ${filename}.`
        });
        return;
      }

      if (dataset === "budget") {
        const rows = await listBudgetRows(scope.profileId, scope.year);
        const text = buildBudgetExportText(rows, delimiter);
        const filename = `wallet-watch-budget-${scope.year}.${format}`;

        downloadTextFile(filename, text, mimeType);
        setExportStatus({
          type: "success",
          message: `${t("messages.importExportSettings.exported")} ${rows.filter((row) => !row.archived).length} ${t("messages.importExportSettings.budgetRowsTo")} ${filename}.`
        });
        return;
      }

      const rows = await listExpenseRows(scope.profileId, scope.year);
      const text = buildExpenseExportText(rows, delimiter);
      const filename = `wallet-watch-expenses-${scope.year}.${format}`;

      downloadTextFile(filename, text, mimeType);
      setExportStatus({
        type: "success",
        message: `${t("messages.importExportSettings.exported")} ${rows.length} ${t("expenses")} ${t("messages.importExportSettings.to")} ${filename}.`
      });
    } catch (error) {
      const { dataset, format } = parseExportSelection(exportSelectionValue);
      setExportStatus({
        type: "error",
        message: localizeErrorMessage(error, t)
          ?? `${t("messages.importExportSettings.failedToExport")} ${dataset} ${format.toUpperCase()}.`
      });
    }
  }

  return (
    <DashboardPage
      layout={importExportPaneLayout}
      slots={[
        {
          key: "import",
          area: "import",
          content: (
                    <Panel
                      padded
                      className="settings-pane--compact"
                      footerClassName={footerToneClass(importStatus?.type)}
                      header={t("import")}
                      footer={importStatus ? (
                        <StatusMessage type={importStatus.type} message={importStatus.message} />
                      ) : (
                        importFooterMessage
                      )}
            >
              <div class="settings-transfer-pane settings-form-stack">
                <div class="settings-field-row settings-import-dataset-row">
                  <div class="settings-field">
                    <span class="settings-field-label">{t("dataset")}</span>
                    <SegmentedControl
                      options={importDatasetOptions}
                      value={importDataset}
                      onChange={setImportDataset}
                    />
                  </div>
                  {importDataset === "budget" ? (
                    <label class="settings-field settings-import-year-field">
                      <span class="settings-field-label">{t("year")}</span>
                      <input
                        class="settings-text-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{4}"
                        maxLength={4}
                        value={budgetImportYear}
                        onInput={(event) => {
                          setBudgetImportYear(event.currentTarget.value.replace(/[^0-9]/g, "").slice(0, 4));
                        }}
                      />
                    </label>
                  ) : null}
                </div>

                <FileDropZone
                  accept={importDropZoneProps.accept}
                  label={importDropZoneProps.label}
                  note={importDropZoneProps.note}
                  onFileSelected={handleImportFile}
                />
              </div>
            </Panel>
          )
        },
        {
          key: "export",
          area: "export",
          content: (
                    <Panel
                      padded
                      className="settings-pane--compact"
                      footerClassName={footerToneClass(exportStatus?.type)}
                      header={t("export")}
                      footer={exportStatus ? (
                        <StatusMessage type={exportStatus.type} message={exportStatus.message} />
                      ) : (
                        exportFooterMessage
                      )}
            >
              <div class="settings-transfer-pane settings-form-stack">
                <div class="settings-field-row settings-export-row">
                  <label class="settings-field">
                    <span class="settings-field-label">{t("download")}</span>
                    <select
                      class="settings-select"
                      value={exportSelectionValue}
                      onChange={(event) => setExportSelection(event.currentTarget.value)}
                    >
                      {exportOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div class="settings-actions">
                  <button
                    class="settings-action-button primary"
                    type="button"
                    onClick={handleExport}
                  >
                    {t("download")}
                  </button>
                  </div>
                </div>
              </div>
            </Panel>
          )
        }
      ]}
    />
  );
}
